<script lang="ts" setup>
// @ts-expect-error JS library
import Player from '@vimeo/player'
import type { Asset, Media, Repo, Skill, SkillsOnRepos, TopicsOnRepos } from '@prisma/client'
import { Atropos } from 'atropos/vue'
import { storeToRefs } from 'pinia'
import VueMarkdown from 'vue-markdown-render'
import type { ComputedRef } from 'vue'
import { Size } from '~/types/custom'
import { useGit } from '~/store/git'
import type { LordIcon } from '~/types/assets'
import { LordIconSource, LordIconTrigger, Iconify } from '~/types/assets'
import type { Table } from '~/types/custom'
import { useRoot } from '~/store'
import { useRadar } from '~/store/radar'
import { useLocaleThemePath } from '~/composables/locale'
import { capitalize } from '~/utils'

const page = '_repo'

const $radar = useRadar()
const { setWheelsCount } = useRoot()
const { routeRaw } = storeToRefs($radar)
const { t } = useI18n()
const i18nHead = useLocaleHead()

const player = ref(null)
const playerEl = ref(null)
const repoSlug = routeRaw.value?.params?.repo || null

const { data: repo, error } = await useAsyncData(async () => await $fetch(`/api/repo/${repoSlug}`))

if (!repo.value || error.value) {
  const event = useRequestEvent()
  if (event) setResponseStatus(event, 404, 'Repo not found.')
}

$radar.setPreviousRoutePath('/git')

useSeoMeta({
  title: () => t(`routes.${page}.title`, { name: capitalize(repo.value?.name) }),
  ogTitle: () => t(`routes.${page}.title`, { name: capitalize(repo.value?.name) }),
  description: () =>
    t(`routes.${page}.description`, {
      name: capitalize(repo.value?.name),
      description: repo?.value?.description
    }),
  ogDescription: () =>
    t(`routes.${page}.description`, {
      name: capitalize(repo.value?.name),
      description: repo?.value?.description
    })
})

useHead(i18nHead.value)

useStructuredData(page)
if (repo.value)
  useSDRepository(
    repo.value as unknown as Repo & {
      media: Media | null
      skills: (SkillsOnRepos & {
        skill: Skill
      })[]
      asset: Asset | null
      topics: TopicsOnRepos[]
    }
  )

const mediaId = computed((): number | null => {
  const chunks = repo.value?.media?.uri?.split('/')
  return chunks && Array.isArray(chunks) ? Number.parseInt(chunks?.[chunks.length - 1]) : null
})

const isGitlab = computed(() => repo?.value?.provider?.toLowerCase() === 'gitlab')

const providerIcon = computed(() => (isGitlab.value ? Iconify?.gitlab : Iconify?.github || ''))

const table: ComputedRef<Table> = computed(
  (): Table => [
    {
      key: t('_repo.table.keys.author'),
      value: 'Lorenzo Rottigni'
    },
    {
      key: t('_repo.table.keys.created-at'),
      // TODO: DayJS handlure
      value: repo?.value?.createdAt?.toString()?.slice(0, 10) || '-'
    },
    {
      key: t('_repo.table.keys.updated-at'),
      // TODO: DayJS handlure
      value: repo?.value?.updatedAt?.toString()?.slice(0, 10) || '-'
    },
    {
      key: t('_repo.table.keys.pushed-at'),
      value: repo?.value?.pushedAt?.toString()?.slice(0, 10) || '-'
    },
    {
      key: t('_repo.table.keys.size'),
      value: `${repo?.value?.size} MB`
    },
    {
      key: t('_repo.table.keys.main-lang'),
      value: repo?.value?.language || '-'
    },
    {
      key: t('_repo.table.keys.star-count'),
      value: repo?.value?.stargazersCount?.toString() || '-'
    },
    {
      key: t('_repo.table.keys.default-branch'),
      value: repo?.value?.defaultBranch || '-'
    }
  ]
)

const iconServer: LordIcon = {
  src: LordIconSource.server,
  trigger: LordIconTrigger.loop,
  revert: true,
  delay: 500,
  size: 'w-10 h-10 lg:w-20 lg:h-20'
}

onMounted(() => {
  try {
    if (repo.value) {
      if (mediaId.value && import.meta.client) {
        player.value = new Player(playerEl.value, {
          id: mediaId.value,
          width: 800
        })
      }
      let wheelsCounter = 1
      if (repo?.value?.media) wheelsCounter++
      setWheelsCount(wheelsCounter)
    } else {
      const event = useRequestEvent()
      if (event) setResponseStatus(event, 404)
    }
  } catch (err) {
    console.error(err)
  }
})
</script>

<template lang="pug">
section.repo__details.container-fluid
  .row(v-if="repo")
    .col-12
      .repo__details__hero
        IconLord(v-bind="iconServer")
        h1 {{ repo?.name }}
      .repo__details__body
        .repo__details__body__preview
          Atropos(
            :rotate="true"
            :active-offset="40"
            :shadow-scale="1.05"
          )
            .repo__details__body__preview__wrapper(
              v-if="repo?.asset"
              :class="isGitlab ? 'gitlab' : 'github'"
            )
              NuxtImg(
                :src="repo?.asset?.nuxtPath || repo?.asset?.thumb"
                :provider="repo?.asset?.provider"
                :alt="$t('_repo.image.alt', { repo: repo.name })"
                :title="$t('_repo.image.title', { repo: repo.name })"
                :aria-label="$t('_repo.image.aria-label', { repo: repo.name })"
                :longdesc="$t( \
                  '_repo.image.longdesc', \
                  { \
                    repo: repo.name, \
                    description: repo?.description \
                      ? $t('_repo.image.longdesc-description', { description: repo.description }) \
                      : '', \
                  }, \
                )"
                :width="600"
                height="400"
              )
              .repo__details__body__preview__badge
                Icon(:name="providerIcon")
          .repo__details__body__preview__meta
            .repo__details__body__preview__meta__description
              // !() cause of vite parser error when using &lt; operator
              ul.repo__details__body__preview__meta__description__topics(
                v-if="!(repo?.topics?.length > 3)"
              )
                li(
                  v-for="({ topic }, index) in repo?.topics"
                  :key="`repo-topic-${index}`"
                  :style="{ backgroundColor: topic?.color }"
                ) {{ `#${topic?.name}` }}
              Vue3Marquee.repo__details__body__preview__meta__description__marquee(
                v-else,
                direction="left",
                :pause-on-click="true",
              )
                span(
                  v-for="({ topic }, index) in repo?.topics"
                  :key="`repo-topic-${index}`"
                  :style="{ backgroundColor: topic?.color }"
                ) {{ `#${topic?.name}` }}
              p {{ repo?.description }}
            .repo__details__body__preview__meta__anchors
              NuxtLink(
                :to="repo?.publicUrl"
                target="_blank"
                rel="nofollow"
                :title="$t('_repo.anchors.provider.title', { name: repo?.name, provider: repo?.provider })"
                :aria-label="$t('_repo.anchors.provider.aria-label', { name: repo?.name, provider: repo?.provider })"
              )
                Icon(:name="providerIcon")
                | {{ $t('_repo.anchors.provider.label', { provider: repo?.provider }) }}
          h4(
            v-if="repo?.topicRaw?.includes('boolean')"
          )
            IconBoolean(class-name="w-14 h-14")
            | {{ $t('_repo.boolean') }}
        .repo__details__body__content
          table
            thead
              tr
                th(colspan="2") {{ $t('_repo.repo') }} {{ `#${repo?.uid}` }}
            tbody
              tr(
                v-for="(row, index) in table"
                :key="`row-${index}`"
              )
                td {{ row.key }}
                td {{ row.value }}
          // !() cause of vite parser error when using &lt; operator
          ul(v-if="!(repo?.skills?.length > 3)")
            li(
              v-for="({ skill }, index) in repo?.skills"
              :key="`repo-skill-${index}`"
            )
              CardSkill(:skill="skill", :size="Size?.sm")
          Vue3Marquee.repo__details__body__content__marquee(
            v-else,
            direction="right",
            :pause-on-click="true",
            :pause-on-hover="true"
          )
            CardSkill.mx-2(
              v-for="({ skill }, index) in repo?.skills"
              :key="`repo-skill-${index}`"
              :skill="skill",
              :size="Size?.sm"
            )
  .row(v-else)
    .col-12
      .repo__404
        h1 Error 404 - Repository Not Found.
        p The server failed to retrieve the git repository:
          span {{ repoSlug }}.
        UIGridImage(
          src="https://s3.rottigni.tech/portfolio.rottigni.tech/static_content/404/hero-404-3.webp"
          :height="4"
        )
        NuxtLink(:to="useLocaleThemePath(`/`)") Home
  .row(
    v-if="repo?.media"
  )
    .col-12
      .repo__details__media
        .repo__details__media__player
          NuxtLink.text-white.text-center(
            :to="repo?.media?.link",
            target="_blank",
            rel="nofollow,"
            :title="$t('_repo.anchors.media.title', { name: repo?.name })"
            :aria-label="$t('_repo.anchors.media.aria-label', { name: repo?.name })"
          )
            h6 {{ $t('_repo.anchors.media.label') }}
            Icon(:name="Iconify?.vimeo || ''")
          .media-player(ref="playerEl")
  .row(
    v-if="repo?.readmemd"
  )
    .col-12
      .repo__details__readme
        h2 {{ $t('_repo.readme') }}
        .repo__details__readme__content
          VueMarkdown( :source="repo.readmemd" :options="{ html: true, breaks: true, linkify: true, typografer: true}")
</template>

<style lang="sass" scoped>
section.repo__details
  @apply w-full h-full flex-center flex-col gap-4 relative overflow-y-auto overflow-x-hidden shadow-none p-0 py-16 md:py-0
  .row
    @apply w-full lg:h-[100dvh] flex-center mt-8 lg:mt-0
  .repo__details__hero
    @apply w-full py-2 flex-center
    h1
      @apply text-2xl lg:text-4xl xl:text-5xl text-white
  .repo__details__body
    @apply w-full h-full flex flex-col lg:flex-row justify-between items-start gap-8
    .repo__details__body__preview
      @apply w-full lg:w-7/12 h-full flex flex-col
      .repo__details__body__preview__wrapper
        @apply w-full h-full relative overflow-visible max-h-[500px] flex justify-center
        .repo__details__body__preview__badge
          @apply absolute left-0 bottom-0 pl-1 pr-2 pt-2 bg-primary rounded-tr-[50%]
          span.iconify
            @apply bg-dark-900
        &.github
          @apply border-2 border-primary
          img
            @apply w-full h-full object-cover
        &.gitlab
          @apply bg-transparent-dark
          img
            @apply w-[450px] h-[450px] object-cover rounded-xl
      h4
        @apply w-fit text-sm md:text-lg flex items-center font-bold lg:mt-6 lg:-ml-4 justify-self-end text-[#00e165] pr-4 py-0
      .repo__details__body__preview__meta
        @apply w-full flex flex-col lg:flex-row justify-between items-start
        .repo__details__body__preview__meta__description
          .repo__details__body__preview__meta__description__topics
            @apply flex-center justify-start flex-wrap gap-2 mt-6
            li
              @apply py-1 px-2 rounded-[30px] font-bold text-xs lg:text-sm
          .repo__details__body__preview__meta__description__marquee
            @apply w-[250px] mt-4 border-x-4 border-black bg-transparent-dark
            span
              @apply py-1 px-2 rounded-[30px] font-bold text-xs lg:text-sm
          p
            @apply text-white mt-4 max-w-[250px]
        .repo__details__body__preview__meta__anchors
          @apply flex items-end flex-col w-fit mt-4
          a
            @apply text-lg text-secondary flex items-center gap-2 whitespace-nowrap
            span.iconify
              @apply bg-primary text-4xl
    .repo__details__body__content
      @apply w-full lg:w-5/12 overflow-hidden
      table
        @apply w-full overflow-auto scrollbar-primary
        tr
          @apply w-full
          td
            @apply text-sm xl:text-base whitespace-nowrap py-0.5
            a
              @apply bg-primary text-dark-900 font-bold
      ul
        @apply w-full flex justify-between gap-4 mt-4
      .repo__details__body__content__marquee
         @apply w-full overflow-hidden mt-4 bg-dark-900
  .repo__details__media
    @apply flex-center flex-col gap-8
    .repo__details__media__player
      @apply w-full bg-dark-800 border-4 border-primary rounded-2xl shadow-2xl
      a
        @apply text-base text-dark-900 flex-center font-bold text-primary py-1
        span.iconify
          @apply bg-dark-900 text-2xl
      .media-player
        @apply w-full max-w-full mx-auto overflow-hidden flex-center pb-4
      :deep(.media-player)
        iframe
          @apply w-full h-[30vh] sm:h-[50vh] bg-dark-800 p-0 m-0 shadow-2xl
  .repo__details__commits
    .repo__details__commits__commit
      @apply text-primary
  .repo__details__readme
    @apply flex-center flex-col items-start -mt-6 w-fit mx-auto min-w-[600px]
    h2
      @apply text-2xl font-bold bg-primary text-dark-900 px-6 rounded-lg translate-y-4
    .repo__details__readme__content
      @apply w-full lg:max-w-[900px] text-white bg-transparent-dark max-h-[65vh] overflow-auto scrollbar-primary border border-primary rounded-md
    :deep(.repo__details__readme__content)
      & > div
        @apply w-full p-4 flex flex-col overflow-hidden
      *
        @apply w-full whitespace-normal overflow-hidden
  .repo__404
    h1
      @apply text-primary text-center text-2xl lg:text-4xl pb-6
    p
      @apply w-full text-lg lg:text-2xl text-white text-center max-w-[600px] mx-auto
      span
        @apply text-secondary mx-1
    a
      @apply block w-full text-center text-primary underline text-2xl
</style>
